import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, AlertTriangle, TrendingDown, TrendingUp, Filter, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { SearchableItemSelect } from '@/components/ui/searchable-item-select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryView = () => {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    warehouse_id: 'all',
    item_id: 'all',
    category: 'all',
    stock_status: 'all' // all, low, ok, overstocked
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [inventory, filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invRes, itemRes, whRes] = await Promise.all([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/items`),
        axios.get(`${API}/warehouses`)
      ]);
      setInventory(invRes.data);
      setItems(itemRes.data);
      setWarehouses(whRes.data);
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...inventory];

    if (filters.warehouse_id && filters.warehouse_id !== 'all') {
      filtered = filtered.filter(inv => inv.warehouse_id === filters.warehouse_id);
    }

    if (filters.item_id && filters.item_id !== 'all') {
      filtered = filtered.filter(inv => inv.item_id === filters.item_id);
    }

    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(inv => {
        const item = items.find(i => i.id === inv.item_id);
        return item?.category === filters.category;
      });
    }

    if (filters.stock_status !== 'all') {
      filtered = filtered.filter(inv => {
        const status = getStockStatus(inv);
        return status === filters.stock_status;
      });
    }

    setFilteredInventory(filtered);
  };

  const clearFilters = () => {
    setFilters({
      warehouse_id: 'all',
      item_id: 'all',
      category: 'all',
      stock_status: 'all'
    });
  };

  const getItemInfo = (id) => {
    const item = items.find(i => i.id === id);
    return item ? `${item.part_number} - ${item.description}` : id;
  };

  const getItemCategory = (id) => {
    const item = items.find(i => i.id === id);
    return item?.category || 'N/A';
  };

  const getWarehouseName = (id) => warehouses.find(w => w.id === id)?.name || id;

  const getStockStatus = (inv) => {
    return inv.status || 'NO_DEMAND';
  };

  const getStockStatusBadge = (inv) => {
    const status = inv.status || 'NO_DEMAND';
    const daysOfStock = inv.days_of_stock || 0;
    
    switch (status) {
      case 'CRITICAL_LOW':
        return <Badge className="bg-red-600 text-white">Critical Low ({Math.round(daysOfStock)}d)</Badge>;
      case 'LOW':
        return <Badge className="bg-yellow-500 text-white">Low ({Math.round(daysOfStock)}d)</Badge>;
      case 'OK':
        return <Badge className="bg-green-500 text-white">OK ({Math.round(daysOfStock)}d)</Badge>;
      case 'OVERSTOCK':
        return <Badge className="bg-blue-500 text-white">Overstock ({Math.round(daysOfStock)}d)</Badge>;
      case 'EXCESS':
        return <Badge className="bg-purple-500 text-white">Excess ({Math.round(daysOfStock)}d)</Badge>;
      case 'NO_DEMAND':
        return <Badge className="bg-gray-400 text-white">No Demand</Badge>;
      default:
        return <Badge className="bg-gray-400 text-white">{status}</Badge>;
    }
  };

  const getStockStatusColor = (status) => {
    switch (status) {
      case 'CRITICAL_LOW': return 'bg-red-600 text-white';
      case 'LOW': return 'bg-yellow-500 text-white';
      case 'OK': return 'bg-green-500 text-white';
      case 'OVERSTOCK': return 'bg-blue-500 text-white';
      case 'EXCESS': return 'bg-purple-500 text-white';
      case 'NO_DEMAND': return 'bg-gray-400 text-white';
      default: return 'bg-gray-400 text-white';
    }
  };

  const getStockLevel = (inv) => {
    const safetyStock = inv.safety_stock || 0;
    const maxStock = safetyStock * 3 || 1000;
    return (inv.qty / maxStock) * 100;
  };

  const hasActiveFilters = (filters.warehouse_id !== '' && filters.warehouse_id !== 'all') ||
    (filters.item_id !== '' && filters.item_id !== 'all') ||
    (filters.category !== '' && filters.category !== 'all') ||
    filters.stock_status !== 'all';

  const uniqueCategories = [...new Set(items.map(i => i.category))];

  // Calculate summary stats
  const criticalLowCount = filteredInventory.filter(inv => inv.status === 'CRITICAL_LOW').length;
  const lowStockCount = filteredInventory.filter(inv => inv.status === 'LOW' || inv.status === 'CRITICAL_LOW').length;
  const okCount = filteredInventory.filter(inv => inv.status === 'OK').length;
  const overstockCount = filteredInventory.filter(inv => inv.status === 'OVERSTOCK' || inv.status === 'EXCESS').length;
  const totalValue = filteredInventory.reduce((sum, inv) => sum + inv.qty, 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Critical Low</p>
                <p className="text-2xl font-bold text-red-600">{criticalLowCount}</p>
                <p className="text-xs text-slate-400">&lt; 2 days stock</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Low Stock</p>
                <p className="text-2xl font-bold text-yellow-600">{lowStockCount}</p>
                <p className="text-xs text-slate-400">2-3 days stock</p>
              </div>
              <TrendingDown className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">OK Stock</p>
                <p className="text-2xl font-bold text-green-600">{okCount}</p>
                <p className="text-xs text-slate-400">3-4 days stock</p>
              </div>
              <Package className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Overstock</p>
                <p className="text-2xl font-bold text-blue-600">{overstockCount}</p>
                <p className="text-xs text-slate-400">&gt; 4 days stock</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total SKUs</p>
                <p className="text-2xl font-bold text-slate-900">{filteredInventory.length}</p>
                <p className="text-xs text-slate-400">Total Qty: {Math.round(totalValue)}</p>
              </div>
              <Package className="w-8 h-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Warehouses</p>
                <p className="text-2xl font-bold text-slate-900">{warehouses.length}</p>
              </div>
              <Package className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="inventory-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-slate-600" />
              <div>
                <CardTitle>Inventory Management</CardTitle>
                <CardDescription>Stock levels, available stock, and safety stock tracking</CardDescription>
              </div>
            </div>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter Section */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-600" />
              <h3 className="font-semibold text-sm text-slate-700">Filters</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block text-slate-600">Warehouse</label>
                <Select
                  data-testid="filter-warehouse"
                  value={filters.warehouse_id}
                  onValueChange={(value) => setFilters({ ...filters, warehouse_id: value })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All warehouses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All warehouses</SelectItem>
                    {warehouses.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-slate-600">Item</label>
                <SearchableItemSelect
                  items={items}
                  value={filters.item_id}
                  onValueChange={(value) => setFilters({ ...filters, item_id: value })}
                  placeholder="All items"
                  dataTestId="filter-item"
                />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-slate-600">Category</label>
                <Select
                  data-testid="filter-category"
                  value={filters.category}
                  onValueChange={(value) => setFilters({ ...filters, category: value })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {uniqueCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block text-slate-600">Stock Status</label>
                <Select
                  data-testid="filter-stock-status"
                  value={filters.stock_status}
                  onValueChange={(value) => setFilters({ ...filters, stock_status: value })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All status</SelectItem>
                    <SelectItem value="CRITICAL_LOW">Critical Low</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="OK">OK</SelectItem>
                    <SelectItem value="OVERSTOCK">Overstock</SelectItem>
                    <SelectItem value="EXCESS">Excess</SelectItem>
                    <SelectItem value="NO_DEMAND">No Demand</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : filteredInventory.length === 0 ? (
            <div data-testid="no-inventory" className="text-center py-8 text-slate-500">
              {hasActiveFilters ? 'No inventory matches the selected filters.' : 'No inventory data available.'}
            </div>
          ) : (
            <div>
              <div className="mb-2 text-sm text-slate-600">
                Showing <strong>{filteredInventory.length}</strong> of <strong>{inventory.length}</strong> items
              </div>
              <div className="overflow-auto max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Warehouse</TableHead>
                      <TableHead className="text-right">Total Stock</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                      <TableHead className="text-right">Parked</TableHead>
                      <TableHead className="text-right">Daily Demand</TableHead>
                      <TableHead className="text-right">Safety Stock</TableHead>
                      <TableHead className="text-right">Days of Stock</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredInventory.map((inv) => {
                      const availableQty = inv.qty || 0;
                      const parkedQty = inv.parked_qty || 0;
                      const totalStock = availableQty + parkedQty;
                      
                      return (
                        <TableRow key={inv.id} data-testid={`inv-row-${inv.id}`}>
                          <TableCell className="font-medium">{getItemInfo(inv.item_id)}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{getItemCategory(inv.item_id)}</Badge>
                          </TableCell>
                          <TableCell>{getWarehouseName(inv.warehouse_id)}</TableCell>
                          <TableCell className="text-right font-semibold">{Math.round(totalStock)}</TableCell>
                          <TableCell className="text-right">
                            <span className={availableQty > 0 ? 'text-green-600' : 'text-red-600'}>
                              {Math.round(availableQty)}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {parkedQty > 0 ? (
                              <span className="text-orange-600 font-semibold">{Math.round(parkedQty)}</span>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {inv.daily_demand ? Math.round(inv.daily_demand) : '0'}
                          </TableCell>
                          <TableCell className="text-right">
                            {inv.safety_stock ? (
                              <span>
                                {Math.round(inv.safety_stock)}
                                {inv.daily_demand > 0 && (
                                  <span className="text-slate-400 text-xs ml-1">({Math.round(inv.safety_stock / inv.daily_demand)}d)</span>
                                )}
                              </span>
                            ) : '0'}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {inv.days_of_stock ? inv.days_of_stock.toFixed(1) : '0'}
                          </TableCell>
                          <TableCell>
                            {getStockStatusBadge(inv)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InventoryView;